import { showNotification } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useRef } from 'react';

import { getBeErrorMessage } from '~/common/utils/getBeErrorMessage';
import { UserContext } from '~/components/providers/UserProvider';
import { walletBalanceQueryKey } from '~/domains/payments/hooks/consts';
import type {
  GroupInvite,
  GroupInviteWithContest,
} from '~/services/marketplace/marketplace-service.schemas';
import {
  acceptGroupInvite,
  cancelEntryGroupInvites,
  declineGroupInvite,
} from '~/services/marketplace/marketplace-service.service.api';

import { GroupEntriesQueryKeys } from '../constants/query';

const showDecisionError = (error: unknown, fallback: string, t: (key: string) => string) => {
  if (axios.isCancel(error)) return;

  if (Array.isArray(error) && error.length > 0) {
    showNotification({
      title: t('contest:common.userStatusModals.locationError.title'),
      message: t('contest:common.userStatusModals.locationError.text'),
      color: 'red',
    });
    return;
  }

  const beMessage = getBeErrorMessage(error);
  if (beMessage) {
    showNotification({ message: beMessage, color: 'red' });
    return;
  }

  if (axios.isAxiosError(error) && !error.response) {
    showNotification({ message: t('notifications.unknownOutcome'), color: 'red' });
    return;
  }

  showNotification({ message: fallback, color: 'red' });
};

const useInviteDecision = ({
  decision,
  onSuccess,
}: {
  decision: 'accepted' | 'declined';
  onSuccess?: (invite: GroupInvite) => void;
}) => {
  const { t } = useTranslation('group');
  const { user } = useContext(UserContext);
  const userId = user?.id ?? '';
  const queryClient = useQueryClient();
  const action = decision === 'accepted' ? acceptGroupInvite : declineGroupInvite;
  // Double-tap guard: set synchronously before the mutation fires, cleared in mutationFn's finally.
  const inFlight = useRef(false);

  const mutation = useMutation({
    cacheTime: 0,
    mutationFn: async (inviteId: string) => {
      try {
        const invite = await action(inviteId);
        if (invite.status !== decision) {
          throw new Error(`Expected invite status ${decision}, received ${invite.status}`);
        }
        return invite;
      } finally {
        inFlight.current = false;
      }
    },
    onSuccess: async (invite) => {
      onSuccess?.(invite);
      // The decision response is contest-less — merge over the cached read, never overwrite it.
      queryClient.setQueryData<GroupInviteWithContest>(
        GroupEntriesQueryKeys.invite(userId, invite.id),
        (cached) => (cached ? { ...invite, contest: cached.contest } : undefined)
      );
      queryClient.setQueryData<readonly GroupInvite[]>(
        GroupEntriesQueryKeys.receivedInvites(userId, 'pending'),
        // Undefined leaves a never-fetched cache untouched; seeding [] would render the home section empty.
        (pendingInvites) => pendingInvites?.filter(({ id }) => id !== invite.id)
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: GroupEntriesQueryKeys.entryViewsForUser(userId),
        }),
        ...(decision === 'accepted'
          ? [queryClient.invalidateQueries({ queryKey: walletBalanceQueryKey(userId) })]
          : []),
      ]);
    },
    onError: (error) =>
      showDecisionError(
        error,
        t(
          decision === 'accepted'
            ? 'notifications.inviteAcceptFailed'
            : 'notifications.inviteDeclineFailed'
        ),
        t
      ),
  });

  const mutate = (inviteId: string, options?: Parameters<typeof mutation.mutate>[1]) => {
    if (inFlight.current) return;
    inFlight.current = true;
    mutation.mutate(inviteId, options);
  };
  const mutateAsync = (inviteId: string, options?: Parameters<typeof mutation.mutateAsync>[1]) => {
    if (inFlight.current) return Promise.reject(new Error('Invite decision in progress'));
    inFlight.current = true;
    return mutation.mutateAsync(inviteId, options);
  };

  return { ...mutation, mutate, mutateAsync };
};

export const useAcceptGroupInvite = ({
  onSuccess,
}: { onSuccess?: (invite: GroupInvite) => void } = {}) =>
  useInviteDecision({ decision: 'accepted', onSuccess });

export const useDeclineGroupInvite = ({
  onSuccess,
}: { onSuccess?: (invite: GroupInvite) => void } = {}) =>
  useInviteDecision({ decision: 'declined', onSuccess });

export const useCancelEntryGroupInvites = ({
  userId,
  onSuccess,
}: {
  userId?: string;
  onSuccess?: () => void;
}) => {
  const { t } = useTranslation('group');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entryId: string) => cancelEntryGroupInvites(entryId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: GroupEntriesQueryKeys.entryViewsForUser(userId ?? ''),
      });
      showNotification({ message: t('notifications.groupCancelled'), color: 'green' });
      onSuccess?.();
    },
    onError: () => {
      showNotification({ message: t('notifications.groupCancelFailed'), color: 'red' });
    },
  });
};
